<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Select, Textarea } from '@drama/ui'
import FormField from '../NovelFormField.vue'
import NovelIssueNote from '../NovelIssueNote.vue'
import { newRelation } from '@drama/novel-authoring'
import { useSectionModel } from './section-model.js'
import './novel-sections.css'

const props = defineProps({
  modelValue: { type: Object, required: true },
  issues: { type: Array, default: () => [] },
  upload: { type: Function, default: null },
})

const emit = defineEmits(['update:modelValue', 'blocked'])
const { t } = useI18n()
const { bind, invalid, notes, removeAt, toggle, write, error } = useSectionModel(props, emit)
const relations = computed(() => props.modelValue.relations ?? [])
const people = computed(() => props.modelValue.characters ?? [])
const personIds = computed(() => people.value.map(person => person.id))
const personOptions = computed(() => people.value.map(person => ({ value: person.id, label: person.name || person.id })))

function personName(person_id) {
  return people.value.find(person => person.id === person_id)?.name || person_id || '—'
}

function relationName(relation) {
  return `${personName(relation.from_id)} → ${personName(relation.to_id)}`
}

function addRelation() {
  write(['relations'], [...relations.value, newRelation(props.modelValue)])
}
</script>

<template>
  <section
    class="novel-entities"
    data-testid="novel-relations-section"
  >
    <header class="novel-entities__header">
      <p>{{ t('novelStudio.fields.count', { count: relations.length }) }}</p>
      <Button
        variant="secondary"
        size="sm"
        data-testid="novel-add-relation"
        @click="addRelation"
      >
        {{ t('novelStudio.fields.addRelation') }}
      </Button>
    </header>
    <p
      v-if="!relations.length"
      class="novel-entities__empty"
    >
      {{ t('novelStudio.empty.relations') }}
    </p>
    <article
      v-for="(relation, index) in relations"
      :key="index"
      class="novel-entity"
      :class="{ 'has-error': invalid(['relations', index]) }"
      :data-path="`relations.${index}`"
      tabindex="-1"
      :data-testid="`novel-relation-${index}`"
    >
      <header class="novel-entity__header">
        <div class="novel-entity__title">
          <h3>{{ relationName(relation) }}</h3>
        </div>
        <Button
          variant="secondary"
          size="sm"
          :aria-label="t('novelStudio.fields.removeNamed', { name: relationName(relation) })"
          @click="removeAt(['relations', index])"
        >
          {{ t('novelStudio.fields.remove') }}
        </Button>
      </header>
      <NovelIssueNote :notes="notes(['relations', index])" />
      <div class="novel-entity__grid">
        <FormField
          :error="error(['relations', index, 'from_id'])"
          required
          :label="t('novelStudio.fields.from')"
        >
          <Select
            :model-value="relation.from_id ?? ''"
            :options="personOptions"
            :aria-label="t('novelStudio.fields.from')"
            :invalid="invalid(['relations', index, 'from_id'])"
            :data-path="`relations.${index}.from_id`"
            block
            @update:model-value="write(['relations', index, 'from_id'], $event)"
          />
        </FormField>
        <FormField
          :error="error(['relations', index, 'to_id'])"
          required
          :label="t('novelStudio.fields.to')"
        >
          <Select
            :model-value="relation.to_id ?? ''"
            :options="personOptions"
            :aria-label="t('novelStudio.fields.to')"
            :invalid="invalid(['relations', index, 'to_id'])"
            :data-path="`relations.${index}.to_id`"
            block
            @update:model-value="write(['relations', index, 'to_id'], $event)"
          />
        </FormField>
        <FormField
          :error="error(['relations', index, 'text'])"
          required
          class="is-wide"
          :label="t('novelStudio.fields.relationText')"
        >
          <Textarea
            v-bind="bind(['relations', index, 'text'])"
            :rows="3"
            :data-testid="`novel-relation-${index}-text`"
          />
        </FormField>
      </div>
      <fieldset
        class="novel-entity__checks"
        :data-path="`relations.${index}.observer_ids`"
      >
        <legend>{{ t('novelStudio.requirements.requiredLabel', { label: t('novelStudio.fields.observers') }) }}</legend>
        <label
          v-for="person in people"
          :key="person.id"
          class="novel-entity__check"
        >
          <input
            type="checkbox"
            :checked="(relation.observer_ids || []).includes(person.id)"
            @change="toggle(['relations', index, 'observer_ids'], person.id, $event.target.checked, personIds)"
          >
          {{ person.name || person.id }}
        </label>
      </fieldset>
    </article>
  </section>
</template>
